import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

const fmt = (v) => `${v < 0 ? "-" : v > 0 ? "+" : ""}$${Math.abs(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const tone = (v) => (v < 0 ? "text-bt-negative" : v > 0 ? "text-bt-positive" : "text-bt-text-tertiary");

export function buildBreakdown(portfolio, trades) {
  const rows = {};
  const row = (s) => (rows[s] ||= { symbol: s, realized: 0, open: 0 });
  trades.forEach(t => { row(t.symbol || "?").realized += t.pnl || 0; });
  portfolio.forEach(p => { row(p.symbol).open += p.pnl || 0; });
  return Object.values(rows)
    .map(r => ({ ...r, total: r.realized + r.open }))
    .sort((a, b) => Math.abs(b.total) - Math.abs(a.total));
}

export function PnlBreakdownPopover({ portfolio, trades, realized, open, children }) {
  const rows = buildBreakdown(portfolio, trades);
  return (
    <Popover>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent align="end" className="w-[calc(100vw-2rem)] max-w-sm p-0 bg-bt-surface border-bt-border-subtle" data-testid="pnl-breakdown-popover">
        <div className="px-4 py-3 border-b border-bt-border-subtle">
          <p className="text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">P&L by symbol</p>
          <p className="text-xs text-bt-text-secondary mt-0.5">Banked = closed trades · Open = positions you still hold</p>
        </div>
        <div className="grid grid-cols-4 gap-2 px-4 py-2 text-[10px] font-mono uppercase tracking-wider text-bt-text-tertiary">
          <span>Symbol</span><span className="text-right">Banked</span><span className="text-right">Open</span><span className="text-right">Total</span>
        </div>
        <div className="max-h-72 overflow-y-auto">
          {rows.length === 0 && <p className="px-4 py-4 text-xs text-bt-text-tertiary" data-testid="pnl-breakdown-empty">No positions or closed trades yet.</p>}
          {rows.map(r => (
            <div key={r.symbol} className="grid grid-cols-4 gap-2 px-4 py-1.5 font-mono text-xs hover:bg-bt-hover transition-colors" data-testid={`pnl-breakdown-row-${r.symbol}`}>
              <span className="text-bt-text-primary font-medium truncate">{r.symbol}</span>
              <span className={`text-right ${tone(r.realized)}`}>{fmt(r.realized)}</span>
              <span className={`text-right ${tone(r.open)}`}>{fmt(r.open)}</span>
              <span className={`text-right font-medium ${tone(r.total)}`}>{fmt(r.total)}</span>
            </div>
          ))}
        </div>
        <div className="grid grid-cols-4 gap-2 px-4 py-2.5 border-t border-bt-border-subtle font-mono text-xs" data-testid="pnl-breakdown-totals">
          <span className="text-bt-text-tertiary uppercase text-[10px] tracking-wider self-center">Total</span>
          <span className={`text-right ${tone(realized)}`}>{fmt(realized)}</span>
          <span className={`text-right ${tone(open)}`}>{fmt(open)}</span>
          <span className={`text-right font-medium ${tone(realized + open)}`}>{fmt(realized + open)}</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}
